import { clamp01, median } from '../core/math';
import { Ring } from '../core/ring';
import { LM, SKELETON } from '../pose/landmarks';
import type { PoseProcessor } from '../pose/processor';
import type { PoseFrame, RawPose } from '../pose/types';
import { DEFENSE_TUNING, PUNCH_TUNING, Recogniser } from '../recog';
import { loadTuning, resetTuning, saveTuning, TUNABLES, tuningIsDefault } from '../recog/tuning';
import { attemptFloor, zeroPeaks, type Peaks } from '../recog/profiler';

/**
 * DIAGNOSTIC & TUNING SCREEN
 * ==========================
 *
 * Built because "it does not read my movements well" is a report I cannot act on.
 * That sentence covers five independent failure stages — tracking, calibration,
 * feature extraction, thresholds, and the game's response — and from the outside
 * they look identical.
 *
 * A live numbers dump narrows it to two of the five. The thing that actually
 * closes the diagnosis is GROUND TRUTH: if the app knows the player was *trying*
 * to throw a jab, it can distinguish
 *
 *     "the threshold is too high"      (signal peaked at 0.14, gate is 0.20)
 * from
 *     "you did not move as much as you think"   (signal peaked at 0.04)
 * from
 *     "it fired but classified it wrong"        (detected, called it a hook)
 *
 * which are three completely different fixes. Hence guided drills rather than a
 * passive readout.
 *
 * Five tools, in the order you should use them:
 *   1. CALIBRATE — the tight loop: move, read what it actually measured, nudge a
 *                  threshold, move again. This is the primary tool; the rest
 *                  exist for when it is not enough.
 *   2. LIVE      — is the skeleton sane, is calibration sane?
 *   3. DRILL     — do a named movement 5x; get detection rate, misclassification
 *                  rate, and which specific gate blocked it.
 *   4. TUNE      — every threshold as a slider, applied instantly, persisted.
 *   5. RECORD    — dump raw landmarks so a session can be replayed offline
 *                  against the real pipeline, turning "it does not work for me"
 *                  into a reproducible test case.
 */

type Tab = 'calibrate' | 'live' | 'drill' | 'tune' | 'record';

/**
 * One movement you can calibrate against, and the gates it has to pass.
 *
 * `floor` is an attempt-detection threshold, deliberately far BELOW the real
 * gate. It exists so the tool can tell "you tried and it did not register" from
 * "you did not try" — without it, a failed movement is indistinguishable from
 * standing still, which is precisely the situation that makes tuning impossible.
 */
interface MoveSpec {
  id: string;
  label: string;
  kind: 'punch' | 'defence';
  expectNumber?: number;
  expectDefKind?: string;
  hint: string;
  gates: Array<{ key: keyof Peaks; label: string; unit: string; tunable: string }>;
}

const PUNCH_GATES: MoveSpec['gates'] = [
  { key: 'extRate', label: 'Arm extension rate', unit: '/s', tunable: 'punch.rateOn' },
  { key: 'gloveSpeed', label: 'Glove speed', unit: 'sw/s', tunable: 'punch.minOnsetSpeed' },
  { key: 'travel', label: 'Arm travel', unit: 'arm', tunable: 'punch.minTravel' },
];
const DEFENCE_GATES: MoveSpec['gates'] = [
  { key: 'headDepth', label: 'Head displacement', unit: 'sw', tunable: 'defence.enter' },
  { key: 'headSpeed', label: 'Head speed', unit: 'sw/s', tunable: 'defence.minSpeed' },
];

const MOVES: MoveSpec[] = [
  { id: 'jab', label: 'Jab', kind: 'punch', expectNumber: 1, hint: 'Lead hand, straight to the head.', gates: PUNCH_GATES },
  { id: 'cross', label: 'Cross', kind: 'punch', expectNumber: 2, hint: 'Rear hand, straight to the head.', gates: PUNCH_GATES },
  { id: 'hook', label: 'Hook', kind: 'punch', expectNumber: 3, hint: 'Elbow up, sweep across.', gates: PUNCH_GATES },
  { id: 'uppercut', label: 'Uppercut', kind: 'punch', expectNumber: 5, hint: 'Drop the hand, drive upward.', gates: PUNCH_GATES },
  { id: 'anypunch', label: 'Any punch', kind: 'punch', hint: 'Any punch at all — just checking it registers.', gates: PUNCH_GATES },
  { id: 'slip', label: 'Slip', kind: 'defence', expectDefKind: 'slip', hint: 'Move your head sideways, then back.', gates: DEFENCE_GATES },
  { id: 'duck', label: 'Duck', kind: 'defence', expectDefKind: 'duck', hint: 'Bend the knees, drop straight down.', gates: DEFENCE_GATES },
  { id: 'roll', label: 'Roll', kind: 'defence', expectDefKind: 'roll', hint: 'Down and across, under a hook.', gates: DEFENCE_GATES },
  { id: 'pull', label: 'Lean back', kind: 'defence', expectDefKind: 'pull', hint: 'Hinge back from the waist, then upright.', gates: DEFENCE_GATES },
];

interface Attempt {
  peaks: Peaks;
  closedAt: number;
  event: { label: string; correct: boolean } | null;
}

interface Drill {
  id: string;
  label: string;
  cue: string;
  kind: 'punch' | 'defence';
  /** Expected boxing number for punches. */
  number?: number;
  /** Expected classification for defensive movements. */
  defKind?: string;
  reps: number;
}

const DRILLS: Drill[] = [
  { id: 'jab', label: 'Jab', cue: 'Throw 5 jabs with your LEAD hand, at head height.', kind: 'punch', number: 1, reps: 5 },
  { id: 'cross', label: 'Cross', cue: 'Throw 5 crosses with your REAR hand, at head height.', kind: 'punch', number: 2, reps: 5 },
  { id: 'hook', label: 'Lead hook', cue: 'Throw 5 lead hooks. Elbow up, sweep across.', kind: 'punch', number: 3, reps: 5 },
  { id: 'uppercut', label: 'Lead uppercut', cue: 'Throw 5 lead uppercuts. Drop the hand, drive up.', kind: 'punch', number: 5, reps: 5 },
  { id: 'body', label: 'Body shot', cue: 'Throw 5 punches to the BODY. Bend the knees.', kind: 'punch', reps: 5 },
  { id: 'slip-left', label: 'Slip left', cue: 'Slip your head to YOUR LEFT 5 times, then back to centre.', kind: 'defence', defKind: 'slip', reps: 5 },
  { id: 'slip-right', label: 'Slip right', cue: 'Slip your head to YOUR RIGHT 5 times, then back to centre.', kind: 'defence', defKind: 'slip', reps: 5 },
  { id: 'duck', label: 'Duck', cue: 'Bend the knees and drop straight down 5 times.', kind: 'defence', defKind: 'duck', reps: 5 },
  { id: 'roll', label: 'Roll', cue: 'Roll under an imaginary hook 5 times — down and across.', kind: 'defence', defKind: 'roll', reps: 5 },
  { id: 'pull', label: 'Lean back', cue: 'Lean back from the waist 5 times, then return upright.', kind: 'defence', defKind: 'pull', reps: 5 },
];

interface GateSample {
  extRate: number;
  onsetSpeed: number;
  travel: number;
  peakSpeed: number;
  headDepth: number;
  headSpeed: number;
}

interface EventRow {
  t: number;
  text: string;
  ok: boolean;
}

const DRILL_MS = 12000;

export class Tuner {
  readonly recog = new Recogniser();
  private root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private tab: Tab = 'live';
  private frame: PoseFrame | null = null;
  private lastDom = 0;

  private bars = new Map<string, { fill: HTMLElement; tick: HTMLElement; val: HTMLElement; row: HTMLElement }>();
  private events: EventRow[] = [];
  private eventList!: HTMLElement;
  private drillPanel!: HTMLElement;
  private tunePanel!: HTMLElement;
  private livePanel!: HTMLElement;
  private calibratePanel!: HTMLElement;
  private recordPanel!: HTMLElement;
  private calibNote!: HTMLElement;

  // Drill state
  private drill: Drill | null = null;
  private drillEndT = 0;
  private drillHits: string[] = [];
  private drillPeaks: GateSample = zeroGates();
  private extWindow = { left: new Ring<number>(20), right: new Ring<number>(20) };

  // Calibrate-loop state
  private move: MoveSpec | null = null;
  private attempts: Attempt[] = [];
  private lastAttempt: Attempt | null = null;
  private live: Peaks = zeroPeaks();
  private activeSince = 0;
  private quietSince = 0;
  private attemptOpen = false;
  /** An event that arrived while an attempt was still open. */
  private pendingEvent: { label: string; correct: boolean } | null = null;

  // Recording
  private recording = false;
  private recorded: Array<{ t: number; world: number[]; image: number[]; q: number }> = [];

  onRecalibrate: (() => void) | null = null;
  onClose: (() => void) | null = null;

  constructor(host: HTMLElement, private processor: PoseProcessor) {
    loadTuning();
    this.root = host;
    this.root.innerHTML = '';
    this.root.className = 'tuner';

    const head = el('div', 'tuner-head');
    head.append(
      btn('← Back', 'ghost', () => this.onClose?.()),
      ...(['calibrate', 'live', 'drill', 'tune', 'record'] as Tab[]).map((t) =>
        btn(t.toUpperCase(), 'chip tab', () => this.setTab(t), `tab-${t}`),
      ),
    );
    this.root.append(head);

    const body = el('div', 'tuner-body');
    const left = el('div', 'tuner-left');
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'tuner-canvas';
    left.append(this.canvas);
    this.calibNote = el('div', 'tuner-calib');
    left.append(this.calibNote);
    body.append(left);

    const right = el('div', 'tuner-right');
    this.calibratePanel = el('div', 'tuner-panel');
    this.livePanel = el('div', 'tuner-panel');
    this.drillPanel = el('div', 'tuner-panel');
    this.tunePanel = el('div', 'tuner-panel');
    this.recordPanel = el('div', 'tuner-panel');
    right.append(this.calibratePanel, this.livePanel, this.drillPanel, this.tunePanel, this.recordPanel);
    body.append(right);
    this.root.append(body);

    const c = this.canvas.getContext('2d');
    if (!c) throw new Error('2D canvas unavailable');
    this.ctx = c;

    this.buildCalibrate();
    this.buildLive();
    this.buildDrill();
    this.buildTune();
    this.buildRecord();
    this.setTab('calibrate');

    this.recog.bus.on('punch', (p) => {
      this.pushEvent(
        `PUNCH ${p.number} ${p.type}${p.target === 'body' ? '/body' : ''} · ${p.side} · conf ${(p.confidence * 100) | 0}% · speed ${p.speed.toFixed(1)}`,
        true,
      );
      if (this.move?.kind === 'punch') {
        const correct = this.move.expectNumber === undefined || p.number === this.move.expectNumber;
        this.attributeEvent(`${p.type} (${p.number})`, correct, p.tPeak);
      }
      if (this.drill?.kind === 'punch') {
        const want = this.drill.number;
        const bodyDrill = this.drill.id === 'body';
        const ok = bodyDrill ? p.target === 'body' : want === undefined || p.number === want;
        this.drillHits.push(ok ? 'ok' : `wrong:${p.number}:${p.type}`);
      }
    });
    this.recog.bus.on('defense', (d) => {
      this.pushEvent(`DEFENCE ${d.kind} ${d.side} · depth ${d.depth.toFixed(2)}sw · conf ${(d.confidence * 100) | 0}%`, true);
      if (this.move?.kind === 'defence') {
        this.attributeEvent(d.kind, d.kind === this.move.expectDefKind, d.tPeak);
      }
      if (this.drill?.kind === 'defence') {
        const ok = d.kind === this.drill.defKind;
        this.drillHits.push(ok ? 'ok' : `wrong:${d.kind}`);
      }
    });
    this.recog.bus.on('footwork', (f) => this.pushEvent(`FOOTWORK ${f.kind} · ${f.magnitude.toFixed(2)}`, true));
  }

  // ------------------------------------------------------------------ layout

  private setTab(t: Tab): void {
    this.tab = t;
    this.calibratePanel.hidden = t !== 'calibrate';
    this.livePanel.hidden = t !== 'live';
    this.drillPanel.hidden = t !== 'drill';
    this.tunePanel.hidden = t !== 'tune';
    this.recordPanel.hidden = t !== 'record';
    for (const id of ['calibrate', 'live', 'drill', 'tune', 'record']) {
      const b = this.root.querySelector(`#tab-${id}`);
      b?.setAttribute('aria-pressed', String(id === t));
    }
  }

  private bar(host: HTMLElement, id: string, label: string, unit = ''): void {
    const row = el('div', 'sig');
    const name = el('span', 'sig-label');
    name.textContent = label;
    const track = el('div', 'sig-track');
    const fill = el('i', 'sig-fill');
    const tick = el('b', 'sig-tick');
    track.append(fill, tick);
    const val = el('span', 'sig-val');
    val.textContent = `– ${unit}`;
    row.append(name, track, val);
    host.append(row);
    this.bars.set(id, { fill, tick, val, row });
  }

  private setBar(id: string, value: number, max: number, threshold: number | null, unit = '', signed = false): void {
    const b = this.bars.get(id);
    if (!b) return;
    const norm = signed ? clamp01((value + max) / (2 * max)) : clamp01(value / max);
    b.fill.style.width = `${norm * 100}%`;
    if (threshold !== null) {
      const tn = signed ? clamp01((threshold + max) / (2 * max)) : clamp01(threshold / max);
      b.tick.style.left = `${tn * 100}%`;
      b.tick.hidden = false;
      const passed = signed ? Math.abs(value) >= threshold : value >= threshold;
      b.fill.classList.toggle('pass', passed);
    } else {
      b.tick.hidden = true;
    }
    b.val.textContent = `${value.toFixed(2)}${unit}`;
  }

  /**
   * THE CALIBRATION LOOP: move → see what it measured → adjust → move again.
   *
   * Three things make this work that the LIVE tab does not do:
   *
   *  1. PEAK-HOLD. A punch peaks for about 120 ms. A live bar tracking the
   *     instantaneous value is physically unreadable — by the time you look, it
   *     is back at zero. The bars here hold the peak of the last attempt until
   *     the next one, so there is something to actually read.
   *
   *  2. ATTEMPT DETECTION. The tool watches for movement using a floor far below
   *     the real gate, so it can tell "you tried and it did not register" from
   *     "you did not try". Without that distinction there is nothing to tune
   *     against, because a failed rep looks exactly like standing still.
   *
   *  3. CONTROLS AND SIGNALS TOGETHER. Adjusting a threshold on one screen and
   *     verifying it on another breaks the loop. The +/- controls sit directly
   *     under the bar they affect, and they are buttons rather than sliders
   *     because you are standing two metres away, not holding the device.
   *
   * Nothing is recorded, stored or uploaded in this mode.
   */
  private buildCalibrate(): void {
    const p = this.calibratePanel;
    p.append(h('Calibrate to your movement'));
    const intro = el('p', 'tuner-help');
    intro.textContent =
      'Pick a movement, then do it. The bars hold what you actually produced so you can read it after the fact. Adjust, do it again, watch the tally. Nothing is recorded.';
    p.append(intro);

    const grid = el('div', 'drill-grid');
    for (const m of MOVES) grid.append(btn(m.label, 'chip', () => this.selectMove(m), `move-${m.id}`));
    p.append(grid);

    const hint = el('p', 'cal-hint');
    hint.id = 'cal-hint';
    p.append(hint);

    const verdict = el('div', 'cal-verdict');
    verdict.id = 'cal-verdict';
    verdict.textContent = 'Waiting for a movement…';
    p.append(verdict);

    const gatesHost = el('div', 'cal-gates');
    gatesHost.id = 'cal-gates';
    p.append(gatesHost);

    const tally = el('div', 'cal-tally');
    tally.id = 'cal-tally';
    p.append(tally);

    const actions = el('div', 'tuner-actions');
    actions.append(
      btn('Fit thresholds to me', 'primary', () => this.fitToMe(), 'cal-fit'),
      btn('Reset to defaults', 'ghost', () => {
        resetTuning();
        this.syncSliders();
        this.renderCalGates();
        this.refreshTuneState();
      }),
    );
    p.append(actions);

    const fitNote = el('p', 'tuner-help');
    fitNote.id = 'cal-fit-note';
    fitNote.textContent =
      '"Fit thresholds to me" sets every gate to 70% of the median you produced across your recent attempts — enough headroom that a slightly lazy rep still counts, without dropping so low that idle fidgeting registers.';
    p.append(fitNote);

    this.selectMove(MOVES[0]);
  }

  private selectMove(m: MoveSpec): void {
    this.move = m;
    this.attempts = [];
    this.lastAttempt = null;
    for (const other of MOVES) {
      const b = this.root.querySelector(`#move-${other.id}`);
      b?.setAttribute('aria-pressed', String(other.id === m.id));
    }
    const hint = this.root.querySelector('#cal-hint');
    if (hint) hint.textContent = m.hint;
    const v = this.root.querySelector('#cal-verdict');
    if (v) {
      v.className = 'cal-verdict';
      v.textContent = 'Waiting for a movement…';
    }
    this.renderCalGates();
    this.renderTally();
  }

  /** Rebuilds the gate rows for the selected movement, with peak-hold bars and +/- controls. */
  private renderCalGates(): void {
    const host = this.root.querySelector('#cal-gates');
    const m = this.move;
    if (!host || !m) return;
    host.innerHTML = '';
    for (const g of m.gates) {
      const t = TUNABLES.find((x) => x.id === g.tunable);
      if (!t) continue;
      const row = el('div', 'cal-gate');

      const top = el('div', 'cal-gate-top');
      const name = el('span', 'cal-gate-name');
      name.textContent = g.label;
      const nums = el('span', 'cal-gate-nums');
      nums.id = `calnum-${g.key}`;
      nums.textContent = `– / ${t.get()}${g.unit}`;
      top.append(name, nums);

      const track = el('div', 'sig-track cal-track');
      const fill = el('i', 'sig-fill');
      fill.id = `calfill-${g.key}`;
      const tick = el('b', 'sig-tick');
      tick.id = `caltick-${g.key}`;
      track.append(fill, tick);

      const ctrl = el('div', 'cal-ctrl');
      const dec = btn('−', 'cal-step', () => this.nudge(t, -1));
      const inc = btn('+', 'cal-step', () => this.nudge(t, +1));
      const val = el('span', 'cal-ctrl-val');
      val.id = `calval-${g.key}`;
      val.textContent = `${round3(t.get())}${g.unit}`;
      ctrl.append(dec, val, inc);

      row.append(top, track, ctrl);
      host.append(row);
    }
  }

  private nudge(t: (typeof TUNABLES)[number], dir: number): void {
    const next = Math.min(t.max, Math.max(t.min, t.get() + dir * t.step));
    t.set(next);
    saveTuning();
    this.syncSliders();
    this.renderCalNumbers();
    this.refreshTuneState();
  }

  /** Updates only the numbers/bars, without rebuilding the DOM. */
  private renderCalNumbers(): void {
    const m = this.move;
    if (!m) return;
    const peaks = this.lastAttempt?.peaks ?? null;
    for (const g of m.gates) {
      const t = TUNABLES.find((x) => x.id === g.tunable);
      if (!t) continue;
      const thr = t.get();
      const peak = peaks ? peaks[g.key] : 0;
      const scale = Math.max(thr * 2, peak * 1.15, 0.001);
      const fill = this.root.querySelector<HTMLElement>(`#calfill-${g.key}`);
      const tick = this.root.querySelector<HTMLElement>(`#caltick-${g.key}`);
      const nums = this.root.querySelector<HTMLElement>(`#calnum-${g.key}`);
      const val = this.root.querySelector<HTMLElement>(`#calval-${g.key}`);
      if (fill) {
        fill.style.width = `${clamp01(peak / scale) * 100}%`;
        fill.classList.toggle('pass', peak >= thr);
        fill.classList.toggle('fail', peaks !== null && peak < thr);
      }
      if (tick) tick.style.left = `${clamp01(thr / scale) * 100}%`;
      if (nums) nums.textContent = `${peaks ? round3(peak) : '–'} / ${round3(thr)}${g.unit}`;
      if (val) val.textContent = `${round3(thr)}${g.unit}`;
    }
  }

  private renderTally(): void {
    const host = this.root.querySelector('#cal-tally');
    if (!host) return;
    const recent = this.attempts.slice(-10);
    if (!recent.length) {
      host.innerHTML = '';
      return;
    }
    const ok = recent.filter((a) => a.event?.correct).length;
    host.innerHTML =
      recent
        .map((a) => `<span class="pip ${a.event ? (a.event.correct ? 'ok' : 'warn') : 'bad'}">${a.event ? (a.event.correct ? '✓' : '~') : '✗'}</span>`)
        .join('') + `<span class="pip-label">${ok}/${recent.length} correct</span>`;
  }

  /**
   * Set each gate to 70% of the median peak this player actually produces.
   * Median rather than mean because one flailing rep should not move the
   * threshold; 70% rather than 100% so a slightly lazy repetition still counts.
   */
  private fitToMe(): void {
    const m = this.move;
    if (!m || this.attempts.length < 3) {
      const note = this.root.querySelector('#cal-fit-note');
      if (note) note.textContent = 'Do the movement at least 3 times first, then fit.';
      return;
    }
    const applied: string[] = [];
    for (const g of m.gates) {
      const t = TUNABLES.find((x) => x.id === g.tunable);
      if (!t) continue;
      const peaks = this.attempts.map((a) => a.peaks[g.key]).filter((v) => v > 0);
      if (peaks.length < 3) continue;
      const target = Math.min(t.max, Math.max(t.min, median(peaks) * 0.7));
      t.set(target);
      applied.push(`${g.label} → ${round3(target)}${g.unit}`);
    }
    saveTuning();
    this.syncSliders();
    this.renderCalNumbers();
    this.refreshTuneState();
    const note = this.root.querySelector('#cal-fit-note');
    if (note) note.textContent = applied.length ? `Applied: ${applied.join(', ')}. Try the movement again.` : 'Not enough clean data yet.';
  }

  private buildLive(): void {
    const p = this.livePanel;
    p.append(h('Tracking'));
    this.bar(p, 'quality', 'Pose quality');
    p.append(h('Lead / left arm'));
    this.bar(p, 'extL', 'Extension');
    this.bar(p, 'rateL', 'Extension rate');
    this.bar(p, 'spdL', 'Glove speed');
    this.bar(p, 'guardL', 'Guard');
    p.append(h('Rear / right arm'));
    this.bar(p, 'extR', 'Extension');
    this.bar(p, 'rateR', 'Extension rate');
    this.bar(p, 'spdR', 'Glove speed');
    this.bar(p, 'guardR', 'Guard');
    p.append(h('Head'));
    this.bar(p, 'headX', 'Sideways (slip)');
    this.bar(p, 'headY', 'Down (duck)');
    this.bar(p, 'pull', 'Back (lean)');
    this.bar(p, 'headDepth', 'Combined depth');
    this.bar(p, 'headSpd', 'Head speed');
    p.append(h('Events'));
    this.eventList = el('div', 'evlist');
    p.append(this.eventList);
  }

  private buildDrill(): void {
    const p = this.drillPanel;
    p.append(h('Guided drills'));
    const intro = el('p', 'tuner-help');
    intro.textContent =
      'Pick a movement and perform it 5 times in 12 seconds. Because the app knows what you were trying to do, it can tell you whether the problem is your threshold, your movement, or the classifier.';
    p.append(intro);

    const grid = el('div', 'drill-grid');
    for (const d of DRILLS) grid.append(btn(d.label, 'chip', () => this.startDrill(d), `drill-${d.id}`));
    p.append(grid);

    const status = el('div', 'drill-status');
    status.id = 'drill-status';
    p.append(status);

    const report = el('div', 'drill-report');
    report.id = 'drill-report';
    p.append(report);
  }

  private buildTune(): void {
    const p = this.tunePanel;
    p.append(h('Thresholds'));
    const note = el('p', 'tuner-help');
    note.textContent =
      'Applied instantly and saved on this device. Every value is in calibrated units (shoulder widths, fractions of your own reach), so they mean the same thing regardless of your size or camera distance.';
    p.append(note);

    let group = '';
    for (const t of TUNABLES) {
      if (t.group !== group) {
        group = t.group;
        p.append(h(group.toUpperCase()));
      }
      const row = el('div', 'tune-row');
      const lab = el('label', 'tune-label');
      lab.textContent = t.label;
      const out = el('span', 'tune-val');
      out.textContent = `${t.get()}${t.unit}`;
      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(t.min);
      input.max = String(t.max);
      input.step = String(t.step);
      input.value = String(t.get());
      input.oninput = () => {
        t.set(Number(input.value));
        out.textContent = `${Number(input.value)}${t.unit}`;
        saveTuning();
        this.refreshTuneState();
      };
      input.dataset.tunable = t.id;
      const help = el('p', 'tune-help');
      help.textContent = t.help;
      lab.append(out);
      row.append(lab, input, help);
      p.append(row);
    }

    const actions = el('div', 'tuner-actions');
    actions.append(
      btn('Reset to defaults', 'ghost', () => {
        resetTuning();
        this.syncSliders();
        this.refreshTuneState();
      }),
      btn('Recalibrate', 'ghost', () => this.onRecalibrate?.()),
    );
    p.append(actions);

    const state = el('p', 'tuner-help');
    state.id = 'tune-state';
    p.append(state);
    this.refreshTuneState();
  }

  private refreshTuneState(): void {
    const s = this.root.querySelector('#tune-state');
    if (s) s.textContent = tuningIsDefault() ? 'Using default thresholds.' : 'Using your custom thresholds (saved on this device).';
  }

  private syncSliders(): void {
    for (const t of TUNABLES) {
      const input = this.root.querySelector<HTMLInputElement>(`input[data-tunable="${t.id}"]`);
      if (input) {
        input.value = String(t.get());
        const out = input.parentElement?.querySelector('.tune-val');
        if (out) out.textContent = `${t.get()}${t.unit}`;
      }
    }
  }

  private buildRecord(): void {
    const p = this.recordPanel;
    p.append(h('Session recording'));
    const note = el('p', 'tuner-help');
    note.textContent =
      'Records the raw landmark stream. The exported file can be replayed through the real recogniser offline, which turns "it does not work for me" into a reproducible test case that can be tuned against without you present. Nothing is uploaded — the file is saved to your device and it is yours to share or not.';
    p.append(note);
    const status = el('p', 'tuner-help');
    status.id = 'rec-status';
    status.textContent = 'Not recording.';
    p.append(status);
    const actions = el('div', 'tuner-actions');
    actions.append(
      btn('Start recording', 'primary', () => this.toggleRecord(), 'rec-toggle'),
      btn('Download JSON', 'ghost', () => this.download()),
    );
    p.append(actions);
  }

  // ------------------------------------------------------------------- drill

  private startDrill(d: Drill): void {
    this.drill = d;
    this.drillEndT = 0;
    this.drillHits = [];
    this.drillPeaks = zeroGates();
    const s = this.root.querySelector('#drill-status');
    if (s) s.textContent = `${d.cue}  —  starting…`;
    const r = this.root.querySelector('#drill-report');
    if (r) r.innerHTML = '';
  }

  private finishDrill(): void {
    const d = this.drill;
    if (!d) return;
    const detected = this.drillHits.length;
    const correct = this.drillHits.filter((x) => x === 'ok').length;
    const wrong = this.drillHits.filter((x) => x !== 'ok');
    const g = this.drillPeaks;
    const lines: string[] = [];

    lines.push(`<b>${d.label}</b> — you were asked for ${d.reps} reps.`);
    lines.push(`Detected <b>${detected}</b> movement${detected === 1 ? '' : 's'}, classified correctly <b>${correct}</b>.`);

    // Blocker analysis: which gate did the movement never pass?
    const gates: Array<{ name: string; peak: number; thr: number; tunable: string }> =
      d.kind === 'punch'
        ? [
            { name: 'extension rate', peak: g.extRate, thr: PUNCH_TUNING.rateOn, tunable: 'punch.rateOn' },
            { name: 'glove speed at start', peak: g.onsetSpeed, thr: PUNCH_TUNING.minOnsetSpeed, tunable: 'punch.minOnsetSpeed' },
            { name: 'arm travel', peak: g.travel, thr: PUNCH_TUNING.minTravel, tunable: 'punch.minTravel' },
            { name: 'peak glove speed', peak: g.peakSpeed, thr: PUNCH_TUNING.minPeakSpeed, tunable: 'punch.minPeakSpeed' },
          ]
        : [
            { name: 'head displacement', peak: g.headDepth, thr: DEFENSE_TUNING.enter, tunable: 'defence.enter' },
            { name: 'head speed', peak: g.headSpeed, thr: DEFENSE_TUNING.minSpeed, tunable: 'defence.minSpeed' },
          ];

    const failed = gates.filter((x) => x.peak < x.thr);

    if (detected === 0) {
      if (failed.length) {
        lines.push('<b>Nothing registered.</b> These gates were never passed:');
        for (const f of failed) {
          lines.push(
            `&nbsp;&nbsp;• <b>${f.name}</b>: your peak was <b>${f.peak.toFixed(2)}</b>, the threshold is <b>${f.thr.toFixed(2)}</b>`,
          );
        }
        lines.push(
          'Either move more sharply, or lower the threshold — the button below sets it to 70% of what you actually produced.',
        );
      } else {
        lines.push(
          '<b>Nothing registered, but every gate was passed.</b> That points at the shape of the movement rather than its size — check the skeleton on the left is tracking your arms, and that calibration is sane.',
        );
      }
    } else if (correct < detected) {
      lines.push(`<b>Misclassified:</b> ${wrong.join(', ')}. The movement is being seen, but read as the wrong technique.`);
      lines.push('This is a classifier issue, not a threshold one — exaggerate the distinguishing part of the movement and re-run.');
    } else if (detected < d.reps) {
      lines.push(`<b>Some reps were missed.</b> Peaks reached: ${gates.map((x) => `${x.name} ${x.peak.toFixed(2)}/${x.thr.toFixed(2)}`).join(', ')}.`);
    } else {
      lines.push('<b>Clean.</b> Every rep was detected and classified correctly.');
    }

    const r = this.root.querySelector('#drill-report');
    if (r) {
      r.innerHTML = lines.map((l) => `<p>${l}</p>`).join('');
      for (const f of failed) {
        if (f.peak <= 0.001) continue;
        const suggested = Math.max(0.01, f.peak * 0.7);
        r.append(
          btn(`Set ${f.name} to ${suggested.toFixed(2)}`, 'ghost', () => {
            const t = TUNABLES.find((x) => x.id === f.tunable);
            if (!t) return;
            t.set(Math.min(t.max, Math.max(t.min, suggested)));
            saveTuning();
            this.syncSliders();
            this.refreshTuneState();
          }),
        );
      }
    }
    const s = this.root.querySelector('#drill-status');
    if (s) s.textContent = 'Done. Pick another drill to continue.';
    this.drill = null;
  }

  // ------------------------------------------------------------------ record

  private toggleRecord(): void {
    this.recording = !this.recording;
    if (this.recording) this.recorded = [];
    const b = this.root.querySelector('#rec-toggle');
    if (b) b.textContent = this.recording ? 'Stop recording' : 'Start recording';
    this.updateRecStatus();
  }

  private updateRecStatus(): void {
    const s = this.root.querySelector('#rec-status');
    if (!s) return;
    const secs = (this.recorded.length / 30).toFixed(1);
    s.textContent = this.recording
      ? `Recording… ${this.recorded.length} frames (~${secs}s)`
      : this.recorded.length
        ? `Captured ${this.recorded.length} frames (~${secs}s). Ready to download.`
        : 'Not recording.';
  }

  private download(): void {
    if (!this.recorded.length) return;
    const payload = {
      version: 1,
      calibration: this.processor.calibration,
      tuning: Object.fromEntries(TUNABLES.map((t) => [t.id, t.get()])),
      frames: this.recorded,
    };
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sparring-session-${Date.now()}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }

  // ------------------------------------------------------------------- frame

  /**
   * Attempt tracking. Opens on ANY movement above a low floor, closes once
   * things go quiet, and reports the peak of every gate over that window —
   * whether or not the recogniser fired. That "whether or not" is the whole
   * point: it is the only way to distinguish a threshold problem from a
   * movement problem.
   */
  private trackAttempt(f: PoseFrame): void {
    const m = this.move;
    if (!m) return;

    let activity: number;
    if (m.kind === 'punch') {
      const l = f.arms.left;
      const r = f.arms.right;
      const arm = l.speed >= r.speed ? l : r;
      const swingSide = l.speed >= r.speed ? 'left' : 'right';
      const xs = this.extWindow[swingSide].toArray();
      const swing = xs.length > 2 ? Math.max(...xs) - Math.min(...xs) : 0;
      activity = arm.speed;
      this.live.extRate = Math.max(this.live.extRate, arm.extensionRate);
      this.live.gloveSpeed = Math.max(this.live.gloveSpeed, arm.speed);
      this.live.travel = Math.max(this.live.travel, swing);
    } else {
      const planar = Math.hypot(f.headOffset.x, f.headOffset.y);
      const depth = Math.max(planar, Math.max(0, f.pullDepth));
      const speed = Math.max(Math.hypot(f.headVel.x, f.headVel.y, f.headVel.z), Math.abs(f.pullRate));
      activity = depth;
      this.live.headDepth = Math.max(this.live.headDepth, depth);
      this.live.headSpeed = Math.max(this.live.headSpeed, speed);
    }

    // The floor must ALWAYS sit below the real gate, otherwise an attempt can
    // only open once it is already passing and the tool could never show
    // "you moved and it did not register" — which is the one thing it exists
    // to show. Derived from the current gate so it survives the user lowering it.
    const floor = attemptFloor(m.kind, m.kind === 'punch' ? PUNCH_TUNING.minOnsetSpeed : DEFENSE_TUNING.enter);

    if (activity > floor) {
      if (!this.attemptOpen) {
        this.attemptOpen = true;
        this.activeSince = f.t;
        this.live = zeroPeaks();
      }
      this.quietSince = 0;
    } else if (this.attemptOpen) {
      if (this.quietSince === 0) this.quietSince = f.t;
      // Close once it has been quiet for a beat, and only if the attempt lasted
      // long enough to be a movement rather than a tracking spike.
      if (f.t - this.quietSince > 280) {
        const lasted = this.quietSince - this.activeSince;
        this.attemptOpen = false;
        this.quietSince = 0;
        if (lasted > 80) {
          const a: Attempt = { peaks: { ...this.live }, closedAt: f.t, event: this.pendingEvent };
          this.pendingEvent = null;
          this.lastAttempt = a;
          this.attempts.push(a);
          if (this.attempts.length > 40) this.attempts.shift();
          this.renderCalNumbers();
          this.renderVerdict();
          this.renderTally();
        }
        this.live = zeroPeaks();
      }
    }
  }

  /** Attach a recogniser event to the attempt it belongs to. */
  private attributeEvent(label: string, correct: boolean, t: number): void {
    if (!this.move) return;
    // Events can land just after the attempt window closes (defence emits on
    // exit), so a short grace period keeps them attached to the right rep.
    const target = this.attemptOpen ? null : this.lastAttempt && t - this.lastAttempt.closedAt < 500 ? this.lastAttempt : null;
    if (target) {
      target.event = { label, correct };
      this.renderVerdict();
      this.renderTally();
      return;
    }
    if (this.attemptOpen) this.pendingEvent = { label, correct };
  }

  private renderVerdict(): void {
    const v = this.root.querySelector('#cal-verdict');
    const a = this.lastAttempt;
    const m = this.move;
    if (!v || !a || !m) return;
    if (a.event) {
      v.className = `cal-verdict ${a.event.correct ? 'ok' : 'warn'}`;
      v.textContent = a.event.correct ? `DETECTED — ${a.event.label}` : `DETECTED, BUT READ AS — ${a.event.label}`;
      return;
    }
    const failed = m.gates.filter((g) => {
      const t = TUNABLES.find((x) => x.id === g.tunable);
      return t && a.peaks[g.key] < t.get();
    });
    v.className = 'cal-verdict bad';
    v.textContent = failed.length
      ? `NOT DETECTED — below: ${failed.map((g) => g.label.toLowerCase()).join(', ')}`
      : 'NOT DETECTED — every gate passed, so this is the shape of the movement, not its size';
  }

  onFrame(f: PoseFrame, raw?: RawPose): void {
    this.frame = f;
    this.recog.update(f);

    // Track the maxima of every gate, even when nothing fires — this is what
    // makes "nothing registered" actionable rather than mysterious.
    for (const side of ['left', 'right'] as const) {
      const a = f.arms[side];
      this.extWindow[side].push(a.extension);
      const xs = this.extWindow[side].toArray();
      const swing = xs.length > 2 ? Math.max(...xs) - Math.min(...xs) : 0;
      if (this.drill?.kind === 'punch') {
        this.drillPeaks.extRate = Math.max(this.drillPeaks.extRate, a.extensionRate);
        this.drillPeaks.onsetSpeed = Math.max(this.drillPeaks.onsetSpeed, a.speed);
        this.drillPeaks.peakSpeed = Math.max(this.drillPeaks.peakSpeed, a.speed);
        this.drillPeaks.travel = Math.max(this.drillPeaks.travel, swing);
      }
    }
    if (this.drill?.kind === 'defence') {
      const planar = Math.hypot(f.headOffset.x, f.headOffset.y);
      const depth = Math.max(planar, Math.max(0, f.pullDepth));
      const speed = Math.max(Math.hypot(f.headVel.x, f.headVel.y, f.headVel.z), Math.abs(f.pullRate));
      this.drillPeaks.headDepth = Math.max(this.drillPeaks.headDepth, depth);
      this.drillPeaks.headSpeed = Math.max(this.drillPeaks.headSpeed, speed);
    }

    if (this.tab === 'calibrate') this.trackAttempt(f);

    if (this.drill) {
      if (this.drillEndT === 0) this.drillEndT = f.t + DRILL_MS;
      const left = Math.max(0, this.drillEndT - f.t);
      const s = this.root.querySelector('#drill-status');
      if (s) s.textContent = `${this.drill.cue}  —  ${(left / 1000).toFixed(1)}s left, ${this.drillHits.length} detected`;
      if (left <= 0) this.finishDrill();
    }

    if (this.recording && raw && this.recorded.length < 30 * 120) {
      // RAW world + image landmarks, deliberately. Recording the derived
      // body-frame values would bake in the current calibration and basis, and
      // a replay could then never test a calibration or a frame-construction
      // change — which are exactly the things most likely to be wrong.
      this.recorded.push({
        t: Math.round(raw.t),
        world: flatVec(raw.world),
        image: flatVec2(raw.image),
        q: Math.round(raw.quality * 100) / 100,
      });
      if (this.recorded.length % 15 === 0) this.updateRecStatus();
    }

    this.draw();
    if (f.t - this.lastDom > 66) {
      this.lastDom = f.t;
      this.updateDom(f);
    }
  }

  private updateDom(f: PoseFrame): void {
    if (this.tab !== 'live') return;
    this.setBar('quality', f.quality, 1, 0.6);
    for (const [side, k] of [
      ['left', 'L'],
      ['right', 'R'],
    ] as const) {
      const a = f.arms[side];
      this.setBar(`ext${k}`, a.extension, 1.1, null);
      this.setBar(`rate${k}`, a.extensionRate, 6, PUNCH_TUNING.rateOn);
      this.setBar(`spd${k}`, a.speed, 8, PUNCH_TUNING.minOnsetSpeed);
      this.setBar(`guard${k}`, a.guard, 1, null);
    }
    const planar = Math.hypot(f.headOffset.x, f.headOffset.y);
    const depth = Math.max(planar, Math.max(0, f.pullDepth));
    const hs = Math.max(Math.hypot(f.headVel.x, f.headVel.y, f.headVel.z), Math.abs(f.pullRate));
    this.setBar('headX', f.headOffset.x, 0.6, DEFENSE_TUNING.enter, 'sw', true);
    this.setBar('headY', -f.headOffset.y, 0.6, DEFENSE_TUNING.enter, 'sw');
    this.setBar('pull', f.pullDepth, 0.6, DEFENSE_TUNING.enter, 'sw');
    this.setBar('headDepth', depth, 0.6, DEFENSE_TUNING.enter, 'sw');
    this.setBar('headSpd', hs, 4, DEFENSE_TUNING.minSpeed);

    this.eventList.innerHTML = this.events
      .slice(-8)
      .reverse()
      .map((e) => `<div class="ev${e.ok ? '' : ' bad'}">${e.text}</div>`)
      .join('');

    this.calibNote.innerHTML = this.calibrationSummary();
  }

  /** Flags calibration values that are physically implausible. */
  private calibrationSummary(): string {
    const c = this.processor.calibration;
    const warn: string[] = [];
    const armL = (c.armLength.left + c.armLength.right) / 2;
    if (!c.done) warn.push('Not calibrated.');
    if (armL < 1.15 || armL > 1.8) warn.push(`Reach looks wrong (${armL.toFixed(2)} sw; expect 1.2–1.7). Recalibrate with arms in a normal guard.`);
    if (c.imageShoulderWidth < 0.08) warn.push('You are very far from the camera — tracking will be poor.');
    if (c.imageShoulderWidth > 0.45) warn.push('You are very close to the camera — your punches may leave the frame.');
    const guardSpread = Math.abs(c.guardHome.left.x - c.guardHome.right.x);
    if (guardSpread > 1.3) warn.push('Calibrated guard is very wide — were your hands up?');
    return (
      `<b>Calibration</b> · stance ${c.stance} · reach ${armL.toFixed(2)} sw · shoulder ${c.shoulderWidth.toFixed(2)} m · ` +
      `distance ${c.homeDistance.toFixed(1)} sw` +
      (warn.length ? `<div class="warn">${warn.join('<br>')}</div>` : '<div class="ok">Looks sane.</div>')
    );
  }

  private pushEvent(text: string, ok: boolean): void {
    this.events.push({ t: performance.now(), text, ok });
    if (this.events.length > 40) this.events.shift();
  }

  /**
   * Left half: the skeleton as the camera sees it. Right half: a body-frame
   * schematic showing where calibration says your head and gloves should sit
   * versus where they are. The schematic is the diagnostic one — a bad
   * calibration is instantly obvious as a large constant offset.
   */
  private draw(): void {
    const f = this.frame;
    const ctx = this.ctx;
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(devicePixelRatio || 1, 2);
    if (this.canvas.width !== Math.round(rect.width * dpr)) {
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
    }
    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#0b0d12';
    ctx.fillRect(0, 0, w, h);
    if (!f) return;

    const halfW = w / 2;

    // --- skeleton (image space, mirrored) ---
    const px = (i: number) => (1 - f.image[i].x) * halfW;
    const py = (i: number) => f.image[i].y * h;
    ctx.lineWidth = 3 * dpr;
    ctx.strokeStyle = 'rgba(90,220,255,0.85)';
    ctx.beginPath();
    for (const [a, b] of SKELETON) {
      ctx.moveTo(px(a), py(a));
      ctx.lineTo(px(b), py(b));
    }
    ctx.stroke();
    for (const i of [LM.LEFT_WRIST, LM.RIGHT_WRIST]) {
      ctx.beginPath();
      ctx.arc(px(i), py(i), 7 * dpr, 0, Math.PI * 2);
      ctx.fillStyle = '#ff5a4a';
      ctx.fill();
    }
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = `${11 * dpr}px ui-monospace, monospace`;
    ctx.fillText('camera view', 8 * dpr, 16 * dpr);

    // --- body-frame schematic ---
    const c = this.processor.calibration;
    const ox = halfW + halfW / 2;
    const oy = h * 0.78;
    const S = h * 0.3; // pixels per shoulder width
    const bx = (x: number) => ox - x * S;
    const by = (y: number) => oy - y * S;

    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1 * dpr;
    ctx.beginPath();
    ctx.moveTo(halfW, 0);
    ctx.lineTo(halfW, h);
    ctx.moveTo(bx(-1.2), by(0));
    ctx.lineTo(bx(1.2), by(0));
    ctx.moveTo(bx(0), by(-0.2));
    ctx.lineTo(bx(0), by(2.1));
    ctx.stroke();

    const ring = (x: number, y: number, r: number, colour: string, dash = false) => {
      ctx.beginPath();
      ctx.setLineDash(dash ? [4 * dpr, 4 * dpr] : []);
      ctx.arc(bx(x), by(y), r * dpr, 0, Math.PI * 2);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 2 * dpr;
      ctx.stroke();
      ctx.setLineDash([]);
    };
    const dot = (x: number, y: number, r: number, colour: string) => {
      ctx.beginPath();
      ctx.arc(bx(x), by(y), r * dpr, 0, Math.PI * 2);
      ctx.fillStyle = colour;
      ctx.fill();
    };

    // Calibrated targets (dashed) vs live positions (solid).
    ring(c.headHome.x, c.headHome.y, 14, 'rgba(255,255,255,0.35)', true);
    ring(c.guardHome.left.x, c.guardHome.left.y, 10, 'rgba(255,180,60,0.35)', true);
    ring(c.guardHome.right.x, c.guardHome.right.y, 10, 'rgba(255,180,60,0.35)', true);

    dot(f.head.x, f.head.y, 9, '#5ce08a');
    dot(f.arms.left.wrist.x, f.arms.left.wrist.y, 8, '#ffb43c');
    dot(f.arms.right.wrist.x, f.arms.right.wrist.y, 8, '#ff8a5a');

    // Head displacement vector — the quantity slips/ducks are gated on.
    ctx.beginPath();
    ctx.moveTo(bx(c.headHome.x), by(c.headHome.y));
    ctx.lineTo(bx(f.head.x), by(f.head.y));
    ctx.strokeStyle = '#5ce08a';
    ctx.lineWidth = 2 * dpr;
    ctx.stroke();

    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillText('body frame (sw) — dashed = calibrated rest', halfW + 8 * dpr, 16 * dpr);
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.fillText(
      `head  x ${f.headOffset.x.toFixed(2)}  y ${f.headOffset.y.toFixed(2)}  pull ${f.pullDepth.toFixed(2)}`,
      halfW + 8 * dpr,
      h - 12 * dpr,
    );
  }
}

const round3 = (v: number) => Math.round(v * 1000) / 1000;

function zeroGates(): GateSample {
  return { extRate: 0, onsetSpeed: 0, travel: 0, peakSpeed: 0, headDepth: 0, headSpeed: 0 };
}

function flatVec(ps: readonly { x: number; y: number; z: number }[]): number[] {
  const out: number[] = [];
  for (const p of ps) out.push(r4(p.x), r4(p.y), r4(p.z));
  return out;
}
function flatVec2(ps: readonly { x: number; y: number }[]): number[] {
  const out: number[] = [];
  for (const p of ps) out.push(r4(p.x), r4(p.y));
  return out;
}
const r4 = (v: number) => Math.round(v * 10000) / 10000;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
}
function h(text: string): HTMLElement {
  const e = el('h4', 'tuner-h');
  e.textContent = text;
  return e;
}
function btn(text: string, cls: string, onClick: () => void, id?: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = cls;
  b.textContent = text;
  b.onclick = onClick;
  if (id) b.id = id;
  return b;
}

export { median };
